import React, { useEffect, useState } from "react";

import { styled, Box, useTheme, StyledEngineProvider } from "@mui/material";
import type { SvgIconProps } from "@mui/material";
import { Outlet, useNavigate } from "react-router-dom";
import type { AppContext, Size, ContextValue, AdminConfig, MissingTransactionData } from "../types";
import { scroller } from "react-scroll";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import { AccountCircleOutlined, LogoutOutlined } from "@mui/icons-material";
import { Toaster } from "react-hot-toast";
import Footer from "./footer";
import { AppWrap, Chevron, LiLink } from "../common/commonComps";
import { clearAllRedirect, currencyFormat, getSid, scrollToTop } from "../common/utils";
import { TOPBARHEIGHT } from "../common/constants";
import navigations from "./account/components/navigation";

import MultiLevelMenu from "./account/components/MultiLevelMenu";
import MissingTransactions from "./account/ShoppingComponents/MissingTransactions";

const Menu = styled(`div`)<{ header_color?: string; open?: boolean }>(({ header_color, open }) => ({
    padding: `90px 40px 0`,
    background: `#f9f9f9`,
    borderLeft: `5px solid ${header_color}`,
    position: `fixed`,
    right: open ? 0 : `-300px`,
    zIndex: 3100,
    width: 300,
    height: `100%`,
    transition: `all 0.5s ease 0s`,
    top: 0,
}));

const Logo = styled(`img`)<{ size?: Size }>({
    display: `inline-block`,
    maxHeight: `100%`,
    maxWidth: `100%`,
    verticalAlign: `top`,
});

const DesktopMenu = styled(`div`)<{ open?: boolean; context: AppContext }>(({ open, context }) => ({
    position: `absolute`,
    left: (document.getElementById(`userbox`)?.getBoundingClientRect().x || 0) + 110,
    background: `white`,
    zIndex: 10000,
    boxShadow: `rgba(0, 0, 0, 0.24) 0px 3px 5px`,
    width: 240,
    overflow: `hidden`,
    borderRadius: `0 0 4px 4px`,
    height: open ? (navigations(context, () => {}).length + 1) * 45 : 0,
    transition: `height 0.5s ease`,
}));

const DMenuItem = styled(`div`)({
    height: 40,
    cursor: `pointer`,
    fontWeight: 400,
    fontSize: 16,
    textAlign: `left`,
    lineHeight: `40px`,
    display: `inline-block`,
    verticalAlign: `middle`,
});

const NavItem = styled(`div`)<{ light?: boolean }>(({ light }) => ({
    display: `block`,
    height: `100%`,
    fontWeight: light ? 500 : 800,
    position: `relative`,

    "& span": {
        position: `absolute`,
        left: `50%`,
        top: `50%`,
        transform: `translate(-50%, -50%)`,
    },

    "&.mobile": {
        height: 45,
        fontSize: 20,
        lineHeight: `45px`,
        width: `calc(100% - 35px)`,
        verticalAlign: `middle`,
        display: `inline-block`,
    },
}));

const UserBox = styled(`div`)<{ size?: Size }>({
    display: `inline-block`,
    verticalAlign: `middle`,
    color: `#333`,
    width: 320,
    lineHeight: `initial`,
    textAlign: `right`,

    "& div, & a": {},
});

const TopBar = styled(`div`)<{ header_color?: string }>({
    height: TOPBARHEIGHT,
    position: `absolute`,
    width: `100%`,
    top: 0,
    left: 0,
    background: `white`,
    borderBottom: `1px solid #ededed`,
});

const Hamburger = styled(`div`)<{ open?: boolean }>(({ theme, open }) => ({
    width: 45,
    top: 30,
    position: `absolute`,
    right: open ? 20 : 5,
    height: 22,
    zIndex: 9999,
    cursor: `pointer`,
    transition: `all 0.4s ease`,

    "& > div": {
        position: `relative`,
        top: 10,
        left: 0,
        background: theme.palette.account.accent || `#666`,
        height: 2,
        width: `60%`,
        transition: `all 0.4s ease`,

        "&::before, &::after": {
            content: `""`,
            position: `absolute`,
            top: -10,
            background: theme.palette.account.accent || `#666`,
            width: `100%`,
            height: 2,
            transition: `all 0.4s ease`,
        },

        "&::after": {
            top: 10,
        },
    },

    ...(open && {
        "& > div": {
            background: `white`,

            "&::before": {
                top: 0,
                transform: `rotate(45deg)`,
                background: `black`,
            },

            "&::after": {
                top: 0,
                transform: `rotate(135deg)`,
                background: `black`,
            },
        },
    }),
}));

const Wrap = styled(`div`)<{ size?: Size }>(({ size }) => ({
    maxWidth: 1440,
    margin: `0 auto`,
    height: `100%`,
    padding: `0 ${size === `small` ? 10 : 20}px`,
    lineHeight: `80px`,
    display: `flex`,
    alignItems: `center`,
    justifyContent: `space-between`,

    "&.nopad": {
        padding: 0,
        maxWidth: 1400,
    },
}));

const EditHeader = styled(`div`)({
    textAlign: `center`,
    lineHeight: 1.5,
    background: `#2f6de9`,
    color: `#333`,
    fontWeight: 700,
    padding: 5,
    width: `100%`,
    position: `fixed`,
    bottom: 0,
    zIndex: 999999,
});

const Overlay = styled(`div`)<{ showOverlay?: boolean; overlayDark?: boolean }>(({ showOverlay, overlayDark }) => ({
    position: `absolute`,
    top: 0,
    height: document.body.scrollHeight,
    left: 0,
    zIndex: 3000,
    right: 0,
    visibility: showOverlay ? `visible` : `hidden`,
    opacity: overlayDark ? 1 : 0,
    background: `rgba(0, 0, 0, 0.35)`,
    transition: `opacity 1s ease`,
}));

const OuterHeader = styled(`div`)<{ tbh: number; size: Size; so?: boolean }>(({ tbh, size }) => ({
    // Preserve original operator precedence: `tbh * size === `small`` parses as `(tbh * size) === `small``.
    // `size` is a non-numeric string, so `tbh * size` is NaN and the comparison is always false — this has
    // always yielded 2. Number()/String() keep the exact NaN→false→2 result while being type-valid (a real
    // fix for this latent precedence bug belongs in a separate, behavioral PR).
    minHeight: `${Math.trunc(String(tbh * Number(size)) === `small` ? 1.5 : 2)}px`,
    width: `100%`,
    top: 0,
    left: 0,
    zIndex: 999999,
    position: `fixed`,
}));

const Alert = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof MuiAlert>>((props, ref) => (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

const Layout = (value: ContextValue) => {
    const { save, size, loaded, context, user } = value;
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const [searchOn, setSearchOn] = useState(false);
    const [dMenuOpen, setDMenuOpen] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [overlayDark, setOverlayDark] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [missingTransaction, setMissingTransaction] = useState<MissingTransactionData | null>(null);

    const theme = useTheme();

    const Col = styled(Box)<{ double?: boolean | string | number; height?: number }>(({ double, height }) => ({
        display: `inline-block`,
        verticalAlign: `middle`,
        "& div": {
            verticalAlign: `middle`,
        },
        height: height ?? (double ? 50 : 25),
        cursor: `pointer`,
        "&.chevron": {
            position: `relative`,
            lineHeight: `${TOPBARHEIGHT}px`,
            height: `${TOPBARHEIGHT}px`,
        },
    }));

    const closeAll = () => {
        setSearchOn(false);
        setDMenuOpen(false);
    };

    useEffect(() => {
        document.body.addEventListener(`click`, closeAll);
        return () => {
            document.body.removeEventListener(`click`, closeAll);
        };
    }, []);

    const openDmenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setDMenuOpen(!dMenuOpen);
    };

    const handleClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === `clickaway`) {
            return;
        }
        setOpen(false);
    };

    const closeMenus = () => {
        if (showOverlay && !overlayDark) return;
        setOverlayDark(false);
        setTimeout(setShowOverlay, 1000);

        setMenuOpen(false);
        document.body.style.overflow = `initial`;
        document.querySelector(`.bm-burger-button`)?.classList.remove(`open`);
    };

    const menuStateChange = () => {
        if (menuOpen) {
            closeMenus();
        } else {
            document.body.style.overflow = `hidden`;
            document.querySelector(`.bm-burger-button`)?.classList.add(`open`);
            setShowOverlay(true);
            setOverlayDark(true);
            setMenuOpen(true);
        }
    };

    const saveConfig = () => {
        save().then(() => {
            setOpen(true);
        });
    };

    const sid = getSid();
    if (!sid) {
        navigate(`/login`);
    }
    const edit = sessionStorage.getItem(`edit`);
    // getItem returns `string | null`; original passed the raw value straight to decodeURIComponent
    // (null coerces to the string "null"). Cast preserves that behavior without adding a guard.
    const programName = decodeURIComponent(sessionStorage.getItem(`programName`) as string);

    const car = () => clearAllRedirect(context);

    return (
        loaded && (
            <Box>
                <Toaster position="bottom-left" />
                <Snackbar
                    anchorOrigin={{ vertical: `top`, horizontal: `center` }}
                    open={open}
                    autoHideDuration={6000}
                    onClose={handleClose}
                >
                    <Alert onClose={handleClose} severity="success" sx={{ width: `100%` }}>
                        Saved
                    </Alert>
                </Snackbar>
                {!!edit && (
                    <EditHeader>
                        You are editing program {programName}. <br />
                        <button type="submit" onClick={saveConfig}>
                            SAVE CHANGES
                        </button>
                    </EditHeader>
                )}

                <OuterHeader {...{ size }} tbh={TOPBARHEIGHT} id="header" so={searchOn}>
                    <TopBar header_color={context.adminConfig?.mall?.main_color} id="topbar">
                        <Wrap size={size}>
                            <LiLink
                                style={{
                                    verticalAlign: `middle`,
                                    margin: `0 20px 0 0`,
                                    width: `${size !== `large` ? 45 : 200}px`,
                                    height: size !== `large` ? `auto` : `${TOPBARHEIGHT - 30}px`,
                                    display: `inline-block`,
                                }}
                                to="/"
                            >
                                <Logo
                                    size={size}
                                    alt="header logo"
                                    src={
                                        // adminConfig is always resolved before render; original accessed it unguarded
                                        size !== `large`
                                            ? (context.adminConfig as AdminConfig).mall?.Color_Mobile_Logo
                                            : (context.adminConfig as AdminConfig).mall?.Color_Desktop_Logo
                                    }
                                />
                            </LiLink>

                            {size === `large` && (
                                <UserBox id="userbox" size={size}>
                                    <Col height={28} onClick={openDmenu}>
                                        <AccountCircleOutlined sx={{ height: 28, width: 28 }} />
                                    </Col>
                                    <Col
                                        double={
                                            !!user?.redeemableAmt && context.adminConfig?.mall?.show_balance_in_top_bar
                                        }
                                        sx={{
                                            textAlign: `left`,
                                            marginLeft: `5px`,

                                            marginRight: `20px`,
                                        }}
                                        onClick={openDmenu}
                                    >
                                        <>
                                            <Box
                                                sx={{
                                                    color: `#333`,
                                                    fontSize: 16,
                                                    fontWeight: 500,
                                                    overflow: `hidden`,
                                                    maxWidth: `100px`,
                                                    display: `inline-block`,
                                                    textOverflow: `ellipsis`,
                                                    whiteSpace: `nowrap`,
                                                    "&:hover": {
                                                        textDecoration: `underline`,
                                                    },
                                                }}
                                            >
                                                {user?.firstName || user?.email?.split(`@`)[0]}
                                            </Box>
                                            <Chevron
                                                top={
                                                    user?.redeemableAmt &&
                                                    context.adminConfig?.mall?.show_balance_in_top_bar
                                                        ? 12
                                                        : 21
                                                }
                                            />
                                            {!!user?.redeemableAmt &&
                                                context.adminConfig?.mall?.show_balance_in_top_bar && (
                                                    <Box sx={{ fontSize: 14, color: `#333` }}>
                                                        ${currencyFormat(user?.redeemableAmt)}
                                                    </Box>
                                                )}
                                        </>
                                    </Col>
                                </UserBox>
                            )}

                            <Overlay {...{ showOverlay, overlayDark }} onClick={closeMenus} />
                            {size !== `large` && (
                                <>
                                    <Hamburger theme={theme} open={menuOpen} onClick={menuStateChange}>
                                        <div />
                                    </Hamburger>
                                    <Menu
                                        header_color={(context.adminConfig as AdminConfig).mall?.main_color}
                                        open={menuOpen}
                                    >
                                        {navigations(context, () => {}).map(({ path, name, icon }) => {
                                            // Rendered nav items always carry an icon; path may be a function
                                            // ("Missing AAA Dollars") but the link coerces it as-is, unchanged from JS.
                                            const Icon = icon as React.ComponentType<SvgIconProps>;
                                            return (
                                                <LiLink
                                                    color="#333"
                                                    onClick={() => {
                                                        scrollToTop();
                                                        menuStateChange();
                                                    }}
                                                    key={path as string}
                                                    to={path as string}
                                                >
                                                    <Icon
                                                        style={{
                                                            verticalAlign: `middle`,
                                                            color: `#333`,
                                                            marginRight: `10px`,
                                                        }}
                                                    />
                                                    <NavItem light sx={{ fontWeight: 500 }} className="mobile">
                                                        {name}
                                                    </NavItem>
                                                </LiLink>
                                            );
                                        })}
                                        <LiLink color="#333" onClick={car} key="logout" to="">
                                            <LogoutOutlined
                                                style={{ verticalAlign: `middle`, color: `#333`, marginRight: `10px` }}
                                            />
                                            <NavItem light className="mobile">
                                                Logout
                                            </NavItem>
                                        </LiLink>
                                    </Menu>
                                </>
                            )}
                        </Wrap>

                        <DesktopMenu context={context} id="desktopmenu" open={dMenuOpen}>
                            {navigations(context, () => {}).map(({ path, name, icon }) => {
                                // Rendered nav items always carry an icon; path may be a function
                                // ("Missing AAA Dollars") but the link coerces it as-is, unchanged from JS.
                                const Icon = icon as React.ComponentType<SvgIconProps>;
                                return (
                                    <LiLink
                                        sx={{ marginTop: `5px` }}
                                        color="#333"
                                        className="dblock bghover"
                                        onClick={() => {
                                            closeAll();
                                            scroller.scrollTo(`top`, {});
                                        }}
                                        key={path as string}
                                        to={path as string}
                                    >
                                        <Icon style={{ verticalAlign: `middle`, marginRight: `15px`, color: `#666` }} />
                                        <DMenuItem>{name}</DMenuItem>
                                    </LiLink>
                                );
                            })}
                            <LiLink color="#333" onClick={car} className="dblock bghover" key="logout" to="">
                                <LogoutOutlined
                                    style={{ verticalAlign: `middle`, marginRight: `15px`, color: `#333` }}
                                />
                                <DMenuItem>Logout</DMenuItem>
                            </LiLink>
                        </DesktopMenu>
                    </TopBar>
                </OuterHeader>
                <StyledEngineProvider injectFirst>
                    <AppWrap sx={{ padding: size === `small` ? `` : `0 20px` }} id="appwrap">
                        {size === `large` && (
                            <MultiLevelMenu
                                {...{ size, context }}
                                // state setter is invoked with the modal payload at runtime; MultiLevelMenu types it as (open?: unknown) => void
                                setMissingTransaction={setMissingTransaction as (open?: unknown) => void}
                            />
                        )}
                        <MissingTransactions {...{ missingTransaction, setMissingTransaction, size, context }} />
                        <Outlet context={[size, setMissingTransaction]} />
                    </AppWrap>
                </StyledEngineProvider>
                {/* adminConfig is always resolved before layout renders (see account/components/navigation) */}
                <Footer {...(context as AppContext & { adminConfig: AdminConfig })} />
            </Box>
        )
    );
};

export default Layout;
